import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { ApiForm, Field, Submit, TextInput, useApiForm } from '../../shared/form';
import { Button } from '../../shared/ui/button';
import { DataPanel } from '../../shared/ui/layout';
import {
  describeResetToken,
  forgotPassword,
  forgotPasswordSchema,
  newPasswordSchema,
  resetPassword,
  type TForgotPassword,
  type TNewPassword,
  type TResetToken,
} from '../../entities/session';
import { LoadingState } from '../../shared/ui/states';
import { clockTime } from '../../shared/lib/format';
import { AuthScreen } from './ui/auth-screen';

const RESEND_SECONDS = 60;

const useCountdown = (from: number) => {
  const [left, setLeft] = useState(from);

  useEffect(() => {
    if (left <= 0) return;
    const timer = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(timer);
  }, [left]);

  return [left, () => setLeft(from)] as const;
};

const clock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${`${seconds % 60}`.padStart(2, '0')}`;

const BackToSignIn = (
  <Link to="/sign-in" className="text-accent">
    Back to sign in
  </Link>
);

const LinkSent = ({ email, onResend }: { email: string; onResend: () => Promise<void> }) => {
  const [left, restart] = useCountdown(RESEND_SECONDS);

  return (
    <AuthScreen
      title="Check your email"
      brand={false}
      description={
        <>
          If an account for <span className="font-mono text-primary">{email}</span> exists, the link
          is on its way. It lasts one hour.
        </>
      }
      footer={BackToSignIn}
    >
      <div className="mt-section flex flex-col gap-field">
        <Button
          variant="ghost"
          block
          disabled={left > 0}
          onClick={async () => {
            await onResend();
            restart();
          }}
        >
          {left > 0 ? `Send again in ${clock(left)}` : 'Send again'}
        </Button>
        <p className="text-meta text-muted">
          Nothing arrived - check spam, or ask an owner to issue a link by hand
        </p>
      </div>
    </AuthScreen>
  );
};

const RequestLink = ({ onSent }: { onSent: (email: string) => void }) => {
  const api = useApiForm<TForgotPassword>({
    schema: forgotPasswordSchema,
    defaultValues: { email: '' },
    onSubmit: async (values) => {
      await forgotPassword(values);
      onSent(values.email);
    },
  });

  return (
    <AuthScreen
      title="Reset password"
      brand={false}
      description="We will send a link for setting a new password. It lasts one hour."
      footer={BackToSignIn}
    >
      <ApiForm api={api}>
        <Field<TForgotPassword> name="email" label="Email">
          <TextInput type="email" autoComplete="email" placeholder="pavel@rawnodes.dev" />
        </Field>
        <Submit waitingLabel="Sending">Send link</Submit>
      </ApiForm>
    </AuthScreen>
  );
};

const Provenance = ({ link }: { link: TResetToken }) => (
  <DataPanel className="mt-5 flex flex-col gap-2">
    {link.emailMasked && (
      <p className="text-meta text-muted">
        For <span className="font-mono text-primary">{link.emailMasked}</span>
        {link.expiresAt && <> · works until {clockTime(link.expiresAt)}</>}
      </p>
    )}
    <p className="text-meta text-muted">
      {link.generatedBy ? (
        <>
          Issued by <span className="text-primary">{link.generatedBy}</span>. If you did not ask for
          it, leave your password alone and tell them.
        </>
      ) : (
        'If you did not ask for this link, leave your password alone and tell an owner of your organization.'
      )}
    </p>
  </DataPanel>
);

const SetPassword = ({ token }: { token: string }) => {
  const navigate = useNavigate();
  const probe = useQuery({
    queryKey: ['reset-token', token],
    queryFn: () => describeResetToken(token),
    retry: false,
  });

  const api = useApiForm<TNewPassword>({
    schema: newPasswordSchema,
    defaultValues: { password: '' },
    onSubmit: async (values) => {
      await resetPassword({ token, password: values.password });
      // Every session was revoked, this one included: sign in with the new one.
      await navigate({ to: '/sign-in' });
    },
  });

  if (probe.isPending) {
    return (
      <AuthScreen title="Checking the link" brand={false}>
        <div className="mt-section">
          <LoadingState rows={2} />
        </div>
      </AuthScreen>
    );
  }

  if (probe.isError || !probe.data?.valid) {
    return (
      <AuthScreen
        title="This link no longer works"
        brand={false}
        description="A reset link lasts one hour and can be used once. Ask for a new one."
        footer={BackToSignIn}
      >
        <div className="mt-section">
          <Link to="/auth/reset-password">
            <Button variant="ghost" block>
              Send a new link
            </Button>
          </Link>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      title="New password"
      brand={false}
      description="The old password stops working on every device, including this one."
    >
      <Provenance link={probe.data} />

      <ApiForm api={api}>
        <Field<TNewPassword> name="password" label="Password">
          <TextInput
            type="password"
            autoComplete="new-password"
            placeholder="8+ characters, a letter and a number"
          />
        </Field>
        <Submit waitingLabel="Saving">Save password</Submit>
      </ApiForm>
    </AuthScreen>
  );
};

export const RecoveryPage = ({ token }: { token?: string }) => {
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (token) return <SetPassword token={token} />;
  if (sentTo) {
    return <LinkSent email={sentTo} onResend={() => forgotPassword({ email: sentTo })} />;
  }
  return <RequestLink onSent={setSentTo} />;
};
